import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Basic translations for error boundary (i18n hooks unavailable in class components)
const ERROR_MESSAGES: Record<string, { title: string; message: string; retry: string }> = {
  en: { title: 'Something went wrong', message: 'An unexpected error occurred. Please try again.', retry: 'Try Again' },
  he: { title: 'משהו השתבש', message: 'אירעה שגיאה בלתי צפויה. נסו שוב.', retry: 'נסו שוב' },
  ar: { title: 'حدث خطأ ما', message: 'حدث خطأ غير متوقع. يرجى المحاولة مرة اخرى.', retry: 'حاول مرة اخرى' },
  es: { title: 'Algo salio mal', message: 'Ocurrio un error inesperado. Intentalo de nuevo.', retry: 'Reintentar' },
  ja: {
    title: '問題が発生しました',
    message: '予期しないエラーが発生しました。もう一度お試しください。',
    retry: '再試行',
  },
};

function getMessages() {
  const lang = (document.documentElement.lang || navigator.language || 'en').split('-')[0] || 'en';
  return ERROR_MESSAGES[lang] || ERROR_MESSAGES['en']!;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const msg = getMessages();

      return (
        <div className="flex min-h-screen items-center justify-center p-6">
          <div className="max-w-sm text-center">
            <h1 className="mb-2 text-xl font-semibold">{msg.title}</h1>
            <p className="mb-4 text-sm text-muted-foreground">{msg.message}</p>
            <button
              onClick={this.handleRetry}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 transition-colors"
            >
              {msg.retry}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
